// src/common/utils/csvExport.js
// Shared value/formatting helpers used by BOTH the per-tab CSV export and the
// all-tabs Excel export, so headers and cell values never drift apart.
//
// Column shape:
//   {
//     key,                       // row property
//     label,                     // header text
//     value?: (row) => any,      // overrides row[key] — nested/derived fields
//     type?: "text" | "date" | "datetime" | "number" | "currency" | "boolean",
//     format?: (raw, row) => any // CSV-only override; wins over `type`
//   }
//
// `type` is what keeps the two exports consistent: CSV renders it as display
// text, Excel writes a real Date/Number with a number format.

export const csvEscape = (v) => {
    if (v == null) return "";
    const s = String(v);
    return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

// Date-only strings are parsed as local midnight so they don't shift a day.
export function parseLocalDate(val) {
    if (!val) return null;
    const m = typeof val === "string" && val.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    return m ? new Date(+m[1], +m[2] - 1, +m[3]) : new Date(val);
}

export const formatDate = (val) => {
    const d = parseLocalDate(val);
    if (!d || Number.isNaN(d.getTime())) return val || "";
    return `${String(d.getMonth() + 1).padStart(2, "0")}/${String(d.getDate()).padStart(2, "0")}/${d.getFullYear()}`;
};

export const formatDateTimeDisplay = (val) => {
    if (!val) return "";
    const d = new Date(val);
    if (Number.isNaN(d.getTime())) return String(val);
    return d.toLocaleString("en-US", {
        month: "2-digit", day: "2-digit", year: "2-digit",
        hour: "2-digit", minute: "2-digit",
    });
};

export const dateStamp = (val = new Date()) => {
    const d = val instanceof Date ? val : parseLocalDate(val) || new Date();
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, "0")}${String(d.getDate()).padStart(2, "0")}`;
};

export const safeFileSegment = (s) =>
    String(s ?? "")
        .trim()
        .replace(/[^\w.-]+/g, "_")
        .replace(/^_+|_+$/g, "")
        .toLowerCase();

const rawCell = (row, col) =>
    typeof col.value === "function" ? col.value(row) : row?.[col.key];

// ── CSV path ───────────────────────────────────────────────────────
export const resolveCell = (row, col) => {
    const raw = rawCell(row, col);
    if (typeof col.format === "function") return col.format(raw, row);
    if (raw == null) return "";
    switch (col.type) {
        case "date":     return formatDate(raw);
        case "datetime": return formatDateTimeDisplay(raw);
        case "boolean":  return raw ? "Yes" : "No";
        default:         return typeof raw === "object" ? JSON.stringify(raw) : raw;
    }
};

export const buildCsv = (columns = [], rows = []) => {
    const header = columns.map((c) => csvEscape(c.label ?? c.key)).join(",");
    const body = rows.map((row) =>
        columns.map((col) => csvEscape(resolveCell(row, col))).join(",")
    );
    // BOM keeps Excel happy with UTF-8 (accented names, ñ, etc.).
    return "\uFEFF" + [header, ...body].join("\r\n");
};

export const downloadBlob = (filename, blob) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
};

export const downloadCsv = (filename, csv) =>
    downloadBlob(filename, new Blob([csv], { type: "text/csv;charset=utf-8;" }));

export const exportRowsToCsv = ({ filename, columns, rows }) => {
    if (!columns?.length || !rows?.length) return;
    downloadCsv(filename, buildCsv(columns, rows));
};

// ── Excel path ─────────────────────────────────────────────────────
// Returns a native type where possible so Excel can sort/filter/sum properly.
export const toExcelValue = (row, col) => {
    const raw = rawCell(row, col);
    if (raw == null || raw === "") return null;

    switch (col.type) {
        case "date": {
            const d = parseLocalDate(raw);
            return d && !Number.isNaN(d.getTime()) ? d : raw;
        }
        case "datetime": {
            const d = new Date(raw);
            return Number.isNaN(d.getTime()) ? raw : d;
        }
        case "number":
        case "currency": {
            const n = Number(String(raw).replace(/[$,\s]/g, ""));
            return Number.isFinite(n) ? n : raw;
        }
        case "boolean":
            return raw ? "Yes" : "No";
        default:
            return typeof raw === "object" ? JSON.stringify(raw) : raw;
    }
};

export const excelNumFmt = (col) => {
    switch (col.type) {
        case "date":     return "mm/dd/yyyy";
        case "datetime": return "mm/dd/yyyy hh:mm AM/PM";
        case "currency": return '"$"#,##0.00';
        case "number":   return "#,##0.##";
        default:         return undefined;
    }
};